import React, { useRef, useState, useEffect } from 'react'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { Box, Card, Grid, Grow, IconButton, InputLabel, MenuItem, Typography } from '@material-ui/core'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { FileIcon } from 'asterX-UI/Icons/FileIcon'
import { BlueLines } from 'asterX-UI/Icons/BlueLines'
import { DeleteIcon } from 'asterX-UI/Icons/DeleteIcon'
import { UploadIcon } from 'asterX-UI/Icons/UploadIcon'
import { useContractStore } from 'context/ContractStore'
import { CollectionService } from 'lib/api/services/collection.service'
import { useSnackbar } from 'notistack'
import { BorderLinearProgress } from './const'
import { useStyles } from './upload-files-modal.styles'

export const UploadFilesModal = ({ open, onClose, onCancel }) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  const intervalRefs = useRef({})
  const { enqueueSnackbar } = useSnackbar()

  const {
    contractStore: { contractAll, fileTypes },
    contractStoreFunc
  } = useContractStore()

  const [category, setCategory] = useState(null)
  const [files, setFiles] = useState([])

  const handleUploadFile = async event => {
    const file = event.target.files?.[0]
    const data = new FormData()
    data.append('file', file)

    const res = await CollectionService.uploadFile(data)

    if (res?.data) {
      const newFile = {
        name: res?.data?.fileName,
        fileDownloadUri: res?.data?.fileDownloadUri,
        size: res?.data?.size,
        date: new Date().toLocaleDateString(),
        progress: 0
      }

      setFiles(prevFiles => [...prevFiles, newFile])

      intervalRefs.current[newFile.name] = setInterval(() => {
        setFiles(prevFiles => {
          const updatedFiles = prevFiles.map(f => {
            if (f.name === newFile.name) {
              const updatedProgress = f.progress + 20
              if (updatedProgress >= 100) {
                clearInterval(intervalRefs.current[newFile.name])
                delete intervalRefs.current[newFile.name]
                return { ...f, progress: 100 }
              }
              return { ...f, progress: updatedProgress }
            }
            return f
          })
          return updatedFiles
        })
      }, 100)
    }
  }

  const handleDelete = fileName => {
    clearInterval(intervalRefs.current[fileName])
    delete intervalRefs.current[fileName]
    setFiles(files.filter(file => file.name !== fileName))
  }

  const handleChangeCategory = e => {
    setCategory(e.target.value)
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleConfirm = async () => {
    if (!contractAll?.id) {
      enqueueSnackbar('нет данных 😿', { variant: 'error' })
      return
    }
    const data = {
      workListId: contractAll?.id,
      fileType: category,
      downloadUrl: files?.[0]?.fileDownloadUri
    }
    const res = await CollectionService.saveWlFile(data)

    if (res?.data) {
      enqueueSnackbar('Успешно 😸', { variant: 'success' })
      contractStoreFunc.getWlFiles(contractAll?.id)
      setCategory(null)
      setFiles([])
      onClose()
    } else {
      enqueueSnackbar(`😿 Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
    }
  }

  const handleClose = () => {
    setCategory(null)
    setFiles([])
    onClose()
  }

  const handleCancel = () => {
    setCategory(null)
    setFiles([])
    onClose()
  }

  useEffect(() => {
    const currentIntervalRefs = intervalRefs.current

    return () => {
      Object.values(currentIntervalRefs).forEach(clearInterval)
    }
  }, [])

  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      title="Загрузка файлов"
      labelConfirm="Загрузить"
      disableConfirm={!category || files?.length === 0}
    >
      <Grid className={classes.wrapper}>
        <Card variant="outlined" className={classes.card}>
          <IconButton className={classes.icon} component="label">
            <UploadIcon />
            <input type="file" hidden onChange={handleUploadFile} ref={fileInputRef} disabled={files?.length === 1} />
          </IconButton>
          <Grid item className={classes.textWrapper} onClick={handleClick}>
            <Typography className={files?.length === 1 ? classes.disabledTitle : classes.title}>
              Нажмите, чтобы загрузить
            </Typography>
            <Typography className={classes.subtitle}>или перетащите документ в эту область</Typography>
          </Grid>
        </Card>
        <Grid item xs={12} className={classes.category}>
          <InputLabel shrink className={classes.inputLabel}>
            Категория
          </InputLabel>
          <SelectPlaceholder
            IconComponent={props => <ExpandMoreIcon {...props} style={{ margin: '0 8px 0 8px' }} />}
            value={category || null}
            onChange={handleChangeCategory}
            fullWidth
            placeholder={'Выберите категорию документов'}
          >
            <MenuItem value={''}>Не выбрано</MenuItem>
            {(fileTypes || []).map(item => (
              <MenuItem key={item?.id} value={item?.code}>
                {item?.name}
              </MenuItem>
            ))}
          </SelectPlaceholder>
        </Grid>

        {(files || []).map(file => (
          <Grow key={file.name} in={file.name} timeout={500}>
            <Grid item container className={classes.fileContainer}>
              <Grid item className={classes.fileIcon}>
                <FileIcon />
                <Box className={classes.blueLines}>
                  <BlueLines />
                </Box>
              </Grid>
              <Grid item xs={10}>
                <Grid item className={classes.fileWrapper}>
                  <Grid item>
                    <Typography variant={'body2'} className={classes.label}>
                      {file.name}
                    </Typography>
                    <Typography variant={'body2'} className={classes.date}>
                      {file.date}
                    </Typography>
                  </Grid>
                  <IconButton onClick={() => handleDelete(file.name)}>
                    <DeleteIcon className={classes.actionBtn} />
                  </IconButton>
                </Grid>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Box width="100%" mr={1}>
                    <BorderLinearProgress variant="determinate" value={file.progress} />
                  </Box>
                  <Box minWidth={35}>
                    <Typography variant="body2">{`${Math.round(file.progress)}%`}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grow>
        ))}
      </Grid>
    </ActionModal>
  )
}
